import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStore } from '../lib/store';
import { Icon } from '../components/Icon';
import { Cover } from '../components/Cover';
import { IconBtn, Progress, TabBar } from '../components/ui';

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function Library() {
  const nav = useNavigate();
  const books = useStore((s) => s.books);
  const session = useStore((s) => s.session);
  const reading = books.find((b) => b.progress > 0) || books[0];
  const continueList = books.filter((b) => b.progress > 0).sort((a, b) => b.progress - a.progress);
  const recentList = [...books].sort((a, b) => b.added_at - a.added_at);

  // First-letter avatar from email
  const initial = (session?.user?.email || 'S')[0].toUpperCase();

  return (
    <div className="flex flex-col h-full safe-top">
      {/* Header */}
      <div className="px-5 pt-3 pb-2 flex items-center justify-between shrink-0">
        <button onClick={() => nav('/search')} className="flex items-center gap-2 h-10 px-4 rounded-full bg-white/80 dark:bg-night-800 shadow-soft text-[13px] text-ink-500 dark:text-ink-300">
          <Icon name="search" className="w-4 h-4" />
          Search library &amp; quotes
        </button>
        <div className="flex gap-2">
          <IconBtn name="upload" onClick={() => nav('/upload')} />
          <button className="w-9 h-9 rounded-full overflow-hidden bg-coral text-cream-50 flex items-center justify-center text-[11px] font-semibold">{initial}</button>
        </div>
      </div>

      {/* Scrollable middle */}
      <div className="flex-1 overflow-y-auto no-scrollbar pb-[180px]">
        <div className="px-5 pt-3">
          <h1 className="font-display text-[34px] leading-[1.05] tracking-tight">
            {greeting()},
            <br />
            <span className="text-ink-500">{(session?.user?.email || 'reader').split('@')[0]} v3.8</span>
          </h1>
          <p className="text-[13px] text-ink-500 mt-1.5 max-w-[260px]">
            {books.length} {books.length === 1 ? 'book' : 'books'} in your library. Read a little today.
          </p>
        </div>

        {/* Streak (zero state, real tracking comes with habits agent) */}
        <div className="px-5 mt-4">
          <div className="rounded-3xl p-4 bg-gradient-to-br from-forest-500 to-forest-600 text-cream-50 shadow-card relative overflow-hidden">
            <div className="absolute -right-4 -top-4 w-28 h-28 rounded-full bg-coral/25 blur-xl" />
            <div className="flex items-center justify-between relative">
              <div>
                <div className="flex items-center gap-2">
                  <Icon name="flame" className="w-4 h-4 text-coral" />
                  <span className="text-[11px] uppercase tracking-[.15em] opacity-80">Reading streak</span>
                </div>
                <div className="flex items-baseline gap-1 mt-1.5">
                  <span className="font-display text-[40px] leading-none">0</span>
                  <span className="text-[13px] opacity-70">days</span>
                </div>
                <div className="text-[11px] opacity-60 mt-1">read 5 min today to start</div>
              </div>
              <div className="grid grid-cols-7 gap-1.5">
                {Array.from({ length: 14 }).map((_, i) => (
                  <div key={i} className="w-3.5 h-3.5 rounded-[5px] bg-cream-50/15" />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Continue reading */}
        {continueList.length > 0 && (
          <div className="mt-6">
            <div className="flex items-end justify-between px-5 mb-3">
              <h2 className="font-display text-[22px]">Continue reading</h2>
            </div>
            <div className="overflow-x-auto no-scrollbar">
              <div className="flex gap-3 px-5 pb-1">
                {continueList.map((b) => (
                  <button key={b.id} onClick={() => nav(`/book/${b.id}`)} className="flex-shrink-0 w-[128px] text-left">
                    <Cover kind={b.cover_kind} dataUrl={b.cover_data} className="w-[128px] h-[190px] rounded-xl" />
                    <div className="mt-2.5 text-[13px] font-medium leading-snug line-clamp-2">{b.title}</div>
                    <div className="text-[11px] text-ink-500 mt-0.5">{b.author}</div>
                    <div className="mt-2"><Progress value={b.progress} /></div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All books grid - everything you've added */}
        <div className="mt-7">
          <div className="flex items-end justify-between px-5 mb-3">
            <h2 className="font-display text-[22px]">Your library</h2>
            <button onClick={() => nav('/upload')} className="text-[12px] text-forest-500 dark:text-forest-400 font-medium flex items-center gap-1">
              <Icon name="plus" className="w-3 h-3" />Add
            </button>
          </div>
          {books.length === 0 ? (
            <div className="px-5">
              <button onClick={() => nav('/upload')} className="w-full rounded-2xl border-2 border-dashed border-ink-300 dark:border-white/15 py-10 text-[13px] text-ink-500">
                No books yet. Tap to add your first.
              </button>
            </div>
          ) : (
            <div className="px-5 grid grid-cols-3 gap-3">
              {recentList.map((b) => (
                <button key={b.id} onClick={() => nav(`/book/${b.id}`)} className="text-left">
                  <Cover kind={b.cover_kind} dataUrl={b.cover_data} className="aspect-[2/3] rounded-xl" />
                  <div className="mt-1.5 text-[12px] font-medium leading-tight line-clamp-2">{b.title}</div>
                  <div className="text-[10px] text-ink-500 truncate">{b.author}</div>
                  {b.progress > 0 ? (
                    <div className="mt-1 flex items-center gap-1.5">
                      <Progress value={b.progress} className="h-[2px] flex-1" />
                      <span className="text-[9px] tabular-nums text-ink-500">{Math.round(b.progress * 100)}%</span>
                    </div>
                  ) : (
                    <div className="mt-1 text-[9px] text-ink-400">Not started</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reading Now sticky strip */}
      {reading && reading.progress > 0 && (
        <div className="absolute left-0 right-0 bottom-[88px] px-4 z-10">
          <motion.button
            onClick={() => nav(`/read/${reading.id}`)}
            whileTap={{ scale: 0.98 }}
            className="w-full rounded-2xl bg-paper dark:bg-night-800 shadow-card p-3 flex items-center gap-3 text-left"
          >
            <Cover kind={reading.cover_kind} dataUrl={reading.cover_data} className="w-12 h-16 rounded-lg shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[.15em] text-forest-500 dark:text-forest-400">Reading now</div>
              <div className="text-[13px] font-semibold truncate">{reading.title}</div>
              <div className="flex items-center gap-2 mt-1">
                <Progress value={reading.progress} className="h-[3px] flex-1" />
                <span className="text-[10px] text-ink-500 tabular-nums">{Math.round(reading.progress * 100)}%</span>
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-ink-900 dark:bg-cream-100 text-cream-50 dark:text-ink-900 flex items-center justify-center">
              <Icon name="play" className="w-3.5 h-3.5 translate-x-[1px]" strokeWidth={0} />
            </div>
          </motion.button>
        </div>
      )}

      <TabBar active="library" />
    </div>
  );
}
